import { Analytics, ConferenceTypeEnum, MediaConstraints } from '@matap/shared';
import { Stream } from './stream';
import { PeerConnection } from './connection/peer.connection';
import { SDP } from './sdp';
import { morphSDP } from './utils/utils';
import { PeerSocket } from './peer.socket';
import { PeerLocalStream } from './peer.local.stream';
import { PeerRemoteStream } from './peer.remote.stream';
import { WebrtcInitializer } from './webrtc.initializer';
import { WebrtcStatsExtractor } from './utils/webrtc.stats.extractor';

export type StreamState = 'connecting' | 'connected' | 'reconnecting';

export interface P2PPeerOptions extends Partial<RTCConfiguration> {
  userId: string;
  conferenceId: string;
  mediaConstraints: MediaConstraints;
  trickleIce: boolean;
  type: ConferenceTypeEnum;
  version: string;
  videoMaxBitrate: number;
  audioMaxBitrate: number;
  preferredCodecs: string[];
  myUserId: string;
  initiatorId: string;
}

export class P2PPeer extends Stream<'change' | 'analytic' | 'quality-change'> {
  public readonly options!: P2PPeerOptions;
  private readonly socket: PeerSocket;
  private readonly localStream: PeerLocalStream;
  private readonly remoteStream: PeerRemoteStream;
  private readonly statsExtractor: WebrtcStatsExtractor;
  public readonly userId: string;
  private readonly initializer: WebrtcInitializer;
  private state: StreamState = 'connecting';
  private peer: PeerConnection;
  private readonly incomingQueue: SDP[] = [];
  private readonly outgoingQueue: SDP[] = [];
  private destroyReason = 'unknown';
  private statsInterval?: ReturnType<typeof setInterval>;

  constructor(stream: PeerLocalStream, options: P2PPeerOptions) {
    super();
    this.userId = options.userId;
    this.options = options;
    this.socket = new PeerSocket(options.conferenceId, options.userId);
    this.localStream = stream;
    this.remoteStream = new PeerRemoteStream(options.userId, options.conferenceId);

    this.peer = this.createPeer();

    this.statsExtractor = new WebrtcStatsExtractor(this.peer.getPeerConnection());

    stream.on('change', this.mLocalStreamListener);
    this.initializer = new WebrtcInitializer(this, this.socket);
    this.initializer.on('analytic', (event) => this.emit('analytic', event));
    this.addListeners();
  }

  public start() {
    if (this.started) return;
    this.started = true;
    this.startStatsCollection();
    this.initializer.setOnBothSidesReady(async () => this.injectQueues()).signalReady();
  }

  private mLocalStreamListener = (stream: MediaStream, oldTrack?: MediaStreamTrack, newTrack?: MediaStreamTrack) => {
    if (oldTrack && newTrack) {
      this.peer.replaceTrack(oldTrack, newTrack, stream);
    }
  };

  private stateChanged = (state: StreamState) => {
    if (this.state === state) return;
    this.emit('analytic', new Analytics.CallStateChange(this.options.conferenceId, { from: this.state, to: state }));
    this.state = state;
    this.emit('change');
  };

  public getState() {
    return this.state;
  }

  public getPeerRemoteStream() {
    return this.remoteStream;
  }

  private injectQueues() {
    this.incomingQueue.forEach((p) => this.onSignal(p));
    this.outgoingQueue.forEach((p) => this.emitSignal(p));
    this.outgoingQueue.length = 0;
    this.incomingQueue.length = 0;
  }

  private createPeer() {
    const { options, localStream, remoteStream } = this;
    const stream = localStream.stream();
    const peer = new PeerConnection({
      sdpTransformer: (sdp: string) => morphSDP(sdp, options),
      streams: stream ? [stream] : [],
      isInitiator: options.initiatorId === options.myUserId,
      trickle: options.trickleIce,
      configuration: {
        iceServers: options.iceServers,
        iceTransportPolicy: options.iceTransportPolicy || 'relay',
      },
      offerOptions: {
        offerToReceiveVideo: options.type === ConferenceTypeEnum.video,
        offerToReceiveAudio: true,
      },
      answerOptions: {
        offerToReceiveVideo: options.type === ConferenceTypeEnum.video,
        offerToReceiveAudio: true,
      },
      signaler: (data) => this.emitSignal(new SDP(data)),
    });

    peer.on('error', (e: any) => {
      this.emit('analytic', new Analytics.Error(this.options.conferenceId, 'SimplePeer Error', e));
    });

    peer.on('stream', (stream: MediaStream) => remoteStream.setRemoteStream(stream));
    peer.on('close', (args) => {
      this.destroyReason = args ?? 'peer closed';
      this.destroy(args);
    });

    return peer;
  }

  private prevRtcConnectionState = 'new';
  private prevIceConnectionState = 'new';
  private prevIceGatheringState = 'new';
  private prevSignalingState = 'stable';

  private async checkConnectionQuality() {
    if (this.getPC().connectionState !== 'connected') {
      return;
    }

    try {
      const isAudioOnly = this.options.type === ConferenceTypeEnum.audio;

      // WebrtcStatsExtractor automatically filters dead data and caches it internally!
      const stats = await this.statsExtractor.extract(isAudioOnly);
      if (!stats) return;

      // Always fire local UI updates
      this.emit('quality-change', stats.quality);

      // Gatekeeper: It will only return a payload if the quality dropped/improved
      const qualityChange = this.statsExtractor.buildAnomalyPayload(isAudioOnly);
      if (qualityChange) {
        this.emit('analytic', new Analytics.QualityChange(this.options.conferenceId, qualityChange));
      }
    } catch (e) {
      console.warn('Failed to extract stats', e);
    }
  }

  private startStatsCollection() {
    this.checkConnectionQuality();
    this.statsInterval = setInterval(() => this.checkConnectionQuality(), 10000);
  }

  private stopStatsCollection() {
    if (this.statsInterval) {
      clearInterval(this.statsInterval);
      this.statsInterval = undefined;
    }
  }

  private onConnectionStateChange = (e: any) => {
    if (this.isDestroyedOrDestroying()) return;
    const s: RTCPeerConnectionState = e.target.connectionState;
    this.emit('analytic', new Analytics.RTCConnectionStateChange(this.options.conferenceId, { from: this.prevRtcConnectionState, to: s }));
    this.prevRtcConnectionState = s;
    if (s === 'disconnected') this.stateChanged('reconnecting');
    else if (s === 'closed' || s === 'failed') {
      this.destroyReason = `Peer state: ${s}`;
      this.destroy(this.destroyReason);
    } else if (s === 'connecting') this.stateChanged('connecting');
    else this.stateChanged('connected');
  };

  private onIceCandidate = (e: RTCPeerConnectionIceEvent) => {
    // Disabled Candidate emissions to save bandwidth
  };

  private onIceCandidateError = (e: RTCPeerConnectionIceErrorEvent) => {
    if (e.errorCode === 701) return;
    this.emit(
      'analytic',
      new Analytics.RTCIceCandidateError(this.options.conferenceId, {
        errorCode: String(e.errorCode),
        errorText: e.errorText,
        hostCandidate: e.address ?? '',
        url: e.url ?? '',
      }),
    );
  };

  private onIceConnectionStateChange = (e: any) => {
    const s = e.target.iceConnectionState;
    this.emit('analytic', new Analytics.RTCIceConnectionStateChange(this.options.conferenceId, { from: this.prevIceConnectionState, to: s }));
    this.prevIceConnectionState = s;
  };

  private onIceGatheringStateChange = (e: any) => {
    const s = e.target.iceGatheringState;
    this.emit('analytic', new Analytics.RTCIceGatheringStateChange(this.options.conferenceId, { from: this.prevIceGatheringState, to: s }));
    this.prevIceGatheringState = s;
  };

  private onSignalingStateChange = (e: any) => {
    const s = e.target.signalingState;
    this.emit('analytic', new Analytics.RTCSignalingStateChanged(this.options.conferenceId, { from: this.prevSignalingState, to: s }));
    this.prevSignalingState = s;
  };

  private onNegotiationNeeded = () => {
    this.emit('analytic', new Analytics.RTCNegotiationNeeded(this.options.conferenceId));
  };

  private emitSignal(sdp: SDP) {
    if (!this.started) {
      this.outgoingQueue.push(sdp);
      return;
    }
    this.socket.emit(sdp);
  }

  public onSignal(sdp: SDP) {
    if (this.isDestroyedOrDestroying()) return;
    if (this.initializer.handleSdp(sdp)) return;
    if (!this.started) {
      this.incomingQueue.push(sdp);
      return;
    }
    this.peer.onRemoteSignal(sdp);
  }

  public getPC(): RTCPeerConnection {
    return this.peer.getPeerConnection();
  }

  protected onDestroy(): void {
    // Pull the final master cached state from the extractor and fire it to the NestJS backend
    const finalStats = this.statsExtractor.lastKnownStats;
    if (finalStats) {
      this.emit('analytic', new Analytics.Stats(this.options.conferenceId, finalStats as any));
    }

    this.emit('analytic', new Analytics.CallClosed(this.options.conferenceId, { reason: this.destroyReason || 'unknown', isFromOtherSide: false }));
    this.stopStatsCollection();
    this.removeListeners();
    this.localStream.off('change', this.mLocalStreamListener);
    this.peer.destroy();
    this.remoteStream.destroy();
    this.statsExtractor.clear();
  }

  private addListeners() {
    const pc = this.getPC();
    if (!pc) return;
    pc.addEventListener('connectionstatechange', this.onConnectionStateChange);
    pc.addEventListener('icecandidate', this.onIceCandidate);
    pc.addEventListener('icecandidateerror', this.onIceCandidateError);
    pc.addEventListener('iceconnectionstatechange', this.onIceConnectionStateChange);
    pc.addEventListener('icegatheringstatechange', this.onIceGatheringStateChange);
    pc.addEventListener('signalingstatechange', this.onSignalingStateChange);
    pc.addEventListener('negotiationneeded', this.onNegotiationNeeded);
  }

  private removeListeners() {
    const pc = this.getPC();
    if (!pc) return;
    pc.removeEventListener('connectionstatechange', this.onConnectionStateChange);
    pc.removeEventListener('icecandidate', this.onIceCandidate);
    pc.removeEventListener('icecandidateerror', this.onIceCandidateError);
    pc.removeEventListener('iceconnectionstatechange', this.onIceConnectionStateChange);
    pc.removeEventListener('icegatheringstatechange', this.onIceGatheringStateChange);
    pc.removeEventListener('signalingstatechange', this.onSignalingStateChange);
    pc.removeEventListener('negotiationneeded', this.onNegotiationNeeded);
  }
}
